import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchMyOrders,
  cancelOrder,
  requestReturnOrder,
  requestReplacementOrder,
  fetchOrderActions,
} from "../../../Redux/custmoer_orders/slice";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getCookie } from "../../../utility/getCookie";
import { startCashfreeCheckout } from "../../../utility/cashfreeCheckout";
import ChatPanel from "../../../ecommerce/globalcomponents/ChatPanel/ChatPanel";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  CreditCard,
  FileText,
  MessageCircleMore,
  PackageCheck,
  RefreshCcw,
  Search,
  X,
} from "lucide-react";
import {
  formatCurrency,
  formatLongOrderDate,
  formatOrderDate,
  mapOrderToOrderRecord,
  toClassSlug,
  OrderRecord,
} from "./ordersAdapters";
import { Order } from "../../../Redux/custmoer_orders/types";
import "./MyOrdersPage.css";

const getProductRoute = (order: any, item: any) => {
  const isReferral =
    order.purchaseType === "MLM" || item.sourceType === "REFERRAL";
  return isReferral
    ? `/Refproductdetails/${item.productId}`
    : `/DetailPage/${item.productId}`;
};

const isPaymentPending = (order: Order): boolean =>
  order.paymentStatus !== "Success" &&
  order.paymentStatus !== "Refunded" &&
  order.paymentStatus !== "COMPLETED" &&
  order.paymentStatus !== "REFUNDED";

const isPaymentSuccess = (order: Order): boolean =>
  order.paymentStatus === "Success" ||
  order.paymentStatus === "COMPLETED";

type OrderActionModalType = "CANCEL" | "RETURN" | "REPLACE";

const MyOrders = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    orders,
    loading,
    error,
    totalPages,
    page,
    actionsMap,
  } = useSelector((state: RootState) => state.customerOrders);

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [selectedLimit, setSelectedLimit] = useState(10);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<OrderActionModalType | null>(
    null,
  );
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Chat Support State
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedOrderForChat, setSelectedOrderForChat] = useState<any | null>(
    null,
  );

  const [retryLoadingId, setRetryLoadingId] = useState<string | null>(null);

  const buildQuery = (pageNum: number) => ({
    page: pageNum,
    limit: selectedLimit,
    startDate: dateRange.from
      ? new Date(dateRange.from).toISOString()
      : undefined,
    endDate: dateRange.to ? new Date(dateRange.to).toISOString() : undefined,
  });

  const handleRetryPayment = async (paymentId: string) => {
    setRetryLoadingId(paymentId);
    try {
      const token = getCookie("access_token");
      const baseUrl = [
        process.env.REACT_APP_API_BASE_URL,
        process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX,
      ]
        .filter(Boolean)
        .join("");

      const response = await axios.post(
        `${baseUrl}/payments/${paymentId}/retry`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data?.success && response.data?.data?.paymentSessionId) {
        await startCashfreeCheckout(
          response.data.data.paymentSessionId,
          response.data.data.paymentMode,
        );
      } else if (response.data?.success && response.data?.data?.redirectUrl) {
        window.location.href = response.data.data.redirectUrl;
      } else if (
        response.data?.success &&
        response.data?.data?.status === "COMPLETED"
      ) {
        alert("Payment completed successfully!");
        dispatch(fetchMyOrders(buildQuery(1)));
      } else {
        alert("Failed to initiate payment retry");
      }
    } catch (err: any) {
      alert(
        err.response?.data?.message ||
          "An error occurred while retrying payment",
      );
    } finally {
      setRetryLoadingId(null);
    }
  };

  useEffect(() => {
    dispatch(fetchMyOrders(buildQuery(1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedLimit, dateRange]);

  // Fetch available actions (cancel/return/replace) for each order in the
  // background. fetchOrderActions is excluded from the slice's global loading
  // matcher, so this doesn't toggle `loading`. Only missing entries are
  // dispatched, so there is no fetch loop.
  useEffect(() => {
    orders.forEach((order) => {
      const orderId = order.orderId || (order as any).id;
      if (orderId && !actionsMap[orderId]) {
        dispatch(fetchOrderActions(orderId));
      }
    });
  }, [orders, actionsMap, dispatch]);

  const handleSearch = () => {
    dispatch(fetchMyOrders(buildQuery(1)));
  };

  const onPageChange = (newPage: number) => {
    dispatch(fetchMyOrders(buildQuery(newPage)));
  };

  const openActionModal = (order: any, type: OrderActionModalType) => {
    setSelectedOrder(order);
    setActionType(type);
    setReason("");
    setDetails("");
    setShowModal(true);
  };

  const openChat = (order: any) => {
    setSelectedOrderForChat(order);
    setChatOpen(true);
  };

  const handleActionSubmit = async () => {
    if (!selectedOrder || !actionType) return;
    if (!reason.trim()) {
      alert("Please provide a reason");
      return;
    }

    const orderId = selectedOrder.orderId || selectedOrder.id;
    setActionLoading(true);

    try {
      if (actionType === "CANCEL") {
        await dispatch(cancelOrder({ orderId, reason })).unwrap();
      } else if (actionType === "RETURN") {
        await dispatch(
          requestReturnOrder({ orderId, reason, details }),
        ).unwrap();
      } else if (actionType === "REPLACE") {
        await dispatch(
          requestReplacementOrder({ orderId, reason, details }),
        ).unwrap();
      }
      setShowModal(false);
      // Refresh available actions for this order (background fetch)
      dispatch(fetchOrderActions(orderId));
    } catch (err: any) {
      alert(err || `Failed to ${actionType.toLowerCase()} order`);
    } finally {
      setActionLoading(false);
    }
  };

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const isSubscriptionOrder = (order: Order) => {
    const item = (order.productDetails || (order as any).items || [])[0];
    const name = item?.productName?.toLowerCase() || "";
    return name.includes("membership") || name.includes("subscription");
  };

  const getOrderDetailRoute = (order: Order) =>
    isSubscriptionOrder(order)
      ? `/SubsOrderDetail`
      : `/OrderDetails/${order.orderId || (order as any).id}`;

  const renderPaymentBadge = (paymentStatus?: string) => {
    if (!paymentStatus) return null;
    return (
      <span
        className={`orders-payment-pill orders-payment-${toClassSlug(
          paymentStatus,
        )}`}
      >
        Payment: {paymentStatus}
      </span>
    );
  };

  const hasActiveAfterSale = (record: OrderRecord, actions: any) => {
    const isActive = (value?: string) =>
      Boolean(value) &&
      value !== "NOT_APPLICABLE" &&
      value !== "NONE";

    return (
      record.refundAmount > 0 ||
      isActive(actions?.refundStatus) ||
      isActive(actions?.returnStatus) ||
      isActive(actions?.replacementStatus)
    );
  };

  const renderDesktopActions = (order: Order, record: OrderRecord) => {
    const orderId = order.orderId || (order as any).id;
    const actions = actionsMap[orderId];
    const firstItem = record.items[0];

    return (
      <aside className="orders-desktop-actions">
        {isPaymentSuccess(order) && (
          <button
            type="button"
            className="primary-action"
            onClick={() => navigate(`/TrackPackage/${orderId}`)}
          >
            Track Order
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(`/seller-feedback/${orderId}`)}
        >
          Seller Feedback
        </button>
        <button
          type="button"
          onClick={() => navigate(`/DeliveryFeedBack`)}
        >
          Delivery Feedback
        </button>
        {firstItem?.productId && (
          <button
            type="button"
            onClick={() =>
              navigate(`/ProductReviewPage/${firstItem.productId}`)
            }
          >
            Write a Product Review
          </button>
        )}
        {actions && (actions.canReturn || actions.canReplace) && (
          <button
            type="button"
            onClick={() => openActionModal(order, "RETURN")}
          >
            Return or Replace Items
          </button>
        )}
        {hasActiveAfterSale(record, actions) && (
          <button
            type="button"
            onClick={() => navigate(`/OrderDetails/${orderId}`)}
          >
            View Return / Refund Status
          </button>
        )}
        <button type="button" onClick={() => openChat(order)}>
          <MessageCircleMore size={13} strokeWidth={2} /> Chat with us
        </button>
      </aside>
    );
  };

  const renderMobileOrderActions = (order: Order, record: OrderRecord) => {
    const orderId = order.orderId || (order as any).id;
    const actions = actionsMap[orderId];
    const needsPayment = isPaymentPending(order);

    return (
      <div className="orders-mobile-order-extras">
        {needsPayment && (
          <button
            type="button"
            className="orders-mobile-pay-button"
            onClick={() => handleRetryPayment(orderId)}
            disabled={retryLoadingId === orderId}
          >
            <CreditCard size={15} strokeWidth={2} />
            {retryLoadingId === orderId ? "Processing..." : "Complete Payment"}
          </button>
        )}
        {actions && (actions.canReturn || actions.canReplace) && (
          <button
            type="button"
            className="orders-mobile-action-button"
            onClick={() => openActionModal(order, "RETURN")}
          >
            Return / Replace
          </button>
        )}
        {hasActiveAfterSale(record, actions) && (
          <button
            type="button"
            className="orders-mobile-action-button"
            onClick={() => navigate(`/OrderDetails/${orderId}`)}
          >
            Return / Refund Status
          </button>
        )}
        <button
          type="button"
          className="orders-mobile-action-button"
          onClick={() => openChat(order)}
        >
          <MessageCircleMore size={14} strokeWidth={2} /> Need Help / Chat
        </button>
      </div>
    );
  };

  const renderDesktop = () => (
    <section className="orders-desktop-page">
      <div className="orders-desktop-shell">
        <div className="orders-desktop-card">
          <header className="orders-desktop-header">
            <div className="orders-desktop-heading">
              <span className="orders-desktop-heading-icon">
                <PackageCheck size={20} strokeWidth={1.9} />
              </span>
              <div>
                <span className="orders-desktop-kicker">MY ACCOUNT</span>
                <h1>Orders List</h1>
                <p>
                  Track purchases, review delivered products and manage order
                  activity.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="orders-help-button"
              onClick={() => openChat(null)}
            >
              <CircleHelp size={15} strokeWidth={1.9} />
              Help with orders
            </button>
          </header>

          <div className="orders-desktop-toolbar">
            <select
              className="orders-page-size"
              value={selectedLimit}
              onChange={(e) => setSelectedLimit(Number(e.target.value))}
              aria-label="Orders per page"
            >
              <option value={10}>Last 10 entries</option>
              <option value={25}>Last 25 entries</option>
              <option value={50}>Last 50 entries</option>
              <option value={75}>Last 75 entries</option>
              <option value={100}>Last 100 entries</option>
            </select>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />

            <span className="orders-desktop-result-pill">
              {orders.length} orders
            </span>

            <div className="orders-desktop-search">
              <Search size={17} strokeWidth={2} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
                placeholder="Search orders..."
                aria-label="Search orders"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    handleSearch();
                  }}
                  aria-label="Clear search"
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>

          <div className="orders-desktop-list">
            {loading ? (
              <div className="orders-desktop-empty">
                <div
                  className="spinner-border text-primary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="orders-desktop-empty">
                <PackageCheck size={30} strokeWidth={1.7} />
                <strong>Something went wrong</strong>
                <span>{error}</span>
              </div>
            ) : orders.length > 0 ? (
              orders.map((order) => {
                const record = mapOrderToOrderRecord(order);
                const needsPayment = isPaymentPending(order);

                return (
                  <article
                    className="orders-desktop-order-card"
                    key={record.orderId}
                  >
                    <div className="orders-desktop-meta">
                      <div className="orders-meta-block">
                        <span>Order placed</span>
                        <strong>{formatOrderDate(record.placedAt)}</strong>
                      </div>
                      <div className="orders-meta-block">
                        <span>Total</span>
                        <strong>{formatCurrency(record.total)}</strong>
                      </div>
                      <div className="orders-meta-block">
                        <span>Ship to</span>
                        <strong className="ship-to-name">
                          {record.shipTo}
                        </strong>
                      </div>
                      <div className="orders-meta-order-number">
                        <span>Order # {record.orderNo || record.orderId}</span>
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(getOrderDetailRoute(order))
                            }
                          >
                            View Order Details
                          </button>
                          {isPaymentSuccess(order) && (
                            <>
                              <span>|</span>
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/invoice/${record.orderId}`)
                                }
                              >
                                Invoice
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="orders-desktop-main">
                      <div className="orders-desktop-product-column">
                        <div className="orders-desktop-status-copy">
                          <div>
                            <h2>{record.statusTitle}</h2>
                            <span
                              className={`orders-status-badge user-orders-status-${toClassSlug(
                                record.status,
                              )}`}
                            >
                              {record.status}
                            </span>
                            {renderPaymentBadge(order.paymentStatus)}
                          </div>
                          <p>{record.statusMessage}</p>
                        </div>

                        {record.items.map((item, itemIdx) => {
                          const rawItem = (
                            order.productDetails ||
                            (order as any).items ||
                            []
                          )[itemIdx];

                          return (
                            <div
                              className="orders-desktop-product"
                              key={item.productId || itemIdx}
                            >
                              <div className="orders-desktop-product-image">
                                <img
                                  src={item.image}
                                  alt={item.productName}
                                  loading="lazy"
                                />
                              </div>
                              <div className="orders-desktop-product-copy">
                                <h3>{item.productName}</h3>
                                <p>
                                  Qty: {item.quantity} | Price:{" "}
                                  {formatCurrency(item.price || record.total)}
                                </p>
                                {item.sellerName && (
                                  <span className="orders-desktop-seller">
                                    Sold by <strong>{item.sellerName}</strong>
                                  </span>
                                )}
                                {order.purchaseType !== "INSTORE" && (
                                  <div className="orders-desktop-product-buttons">
                                    {needsPayment ? (
                                      <button
                                        type="button"
                                        className="orders-buy-again"
                                        onClick={() =>
                                          handleRetryPayment(record.orderId)
                                        }
                                        disabled={
                                          retryLoadingId === record.orderId
                                        }
                                      >
                                        <CreditCard
                                          size={13}
                                          strokeWidth={2}
                                        />
                                        {retryLoadingId === record.orderId
                                          ? "Processing..."
                                          : "Complete Payment"}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="orders-buy-again"
                                        onClick={() =>
                                          navigate(
                                            getProductRoute(order, rawItem),
                                          )
                                        }
                                      >
                                        <RefreshCcw
                                          size={13}
                                          strokeWidth={2}
                                        />
                                        Buy it again
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="orders-view-item"
                                      onClick={() =>
                                        navigate(
                                          getProductRoute(order, rawItem),
                                        )
                                      }
                                    >
                                      View your item
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {renderDesktopActions(order, record)}
                    </div>

                    {record.refundAmount > 0 && (
                      <footer className="orders-desktop-status-footer">
                        <div>
                          <span>Refund Amount</span>
                          <strong>
                            {formatCurrency(record.refundAmount)}
                          </strong>
                        </div>
                        <div>
                          <span>Refund Status</span>
                          <strong
                            className={`status-chip user-orders-refund-${toClassSlug(
                              record.refundStatus,
                            )}`}
                          >
                            {record.refundStatus}
                          </strong>
                        </div>
                        <div>
                          <span>Delivery Status</span>
                          <strong
                            className={`status-chip user-orders-delivery-${toClassSlug(
                              record.deliveryStatus,
                            )}`}
                          >
                            {record.deliveryStatus}
                          </strong>
                        </div>
                        <button
                          type="button"
                          className="orders-view-details-footer"
                          onClick={() =>
                            navigate(getOrderDetailRoute(order))
                          }
                        >
                          <FileText size={14} />
                          View order details
                        </button>
                      </footer>
                    )}
                  </article>
                );
              })
            ) : (
              <div className="orders-desktop-empty">
                <PackageCheck size={30} strokeWidth={1.7} />
                <strong>No orders found</strong>
                <span>Try changing your search or date range.</span>
              </div>
            )}
          </div>

          {!loading && !error && orders.length > 0 && (
            <footer className="orders-desktop-pagination-footer">
              <span>
                Page <strong>{page}</strong> of{" "}
                <strong>{totalPages || 1}</strong>
              </span>
              <div className="orders-desktop-pagination">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={15} />
                </button>
                {pageNumbers.map((pageNum) => (
                  <button
                    type="button"
                    key={pageNum}
                    className={page === pageNum ? "active" : ""}
                    onClick={() => onPageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    onPageChange(Math.min(totalPages, page + 1))
                  }
                  aria-label="Next page"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </footer>
          )}
        </div>
      </div>
    </section>
  );

  const renderMobileList = () => (
    <section className="orders-mobile-page">
      <header className="orders-mobile-list-header">
        <div>
          <span className="orders-mobile-kicker">MY ACCOUNT</span>
          <h1>Your Orders</h1>
          <p>{orders.length} orders found</p>
        </div>
        <span className="orders-mobile-header-icon">
          <PackageCheck size={22} strokeWidth={2} />
        </span>
      </header>

      <div className="orders-mobile-controls">
        <div className="orders-mobile-search">
          <Search size={18} strokeWidth={2} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            placeholder="Search by order or product"
            aria-label="Search orders"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
            >
              <X size={16} strokeWidth={2} />
            </button>
          )}
        </div>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      <div className="orders-mobile-list">
        {loading ? (
          <div className="orders-mobile-empty">
            <span>Loading orders...</span>
          </div>
        ) : error ? (
          <div className="orders-mobile-empty">
            <strong>Something went wrong</strong>
            <span>{error}</span>
          </div>
        ) : orders.length > 0 ? (
          orders.map((order) => {
            const record = mapOrderToOrderRecord(order);
            const item = record.items[0];

            return (
              <div key={record.orderId}>
                <button
                  type="button"
                  className="orders-mobile-order-card"
                  onClick={() => navigate(getOrderDetailRoute(order))}
                >
                  <div className="orders-mobile-order-image">
                    <img src={item?.image} alt="" loading="lazy" />
                  </div>
                  <div className="orders-mobile-order-copy">
                    <div className="orders-mobile-order-topline">
                      <span>ORDER ID</span>
                      <strong>{record.orderNo || record.orderId}</strong>
                    </div>
                    <h2>
                      {item?.productName || "Order"}
                      {record.items.length > 1 &&
                        ` (+${record.items.length - 1} more)`}
                    </h2>
                    <p>{record.statusTitle}</p>
                    <div className="orders-mobile-order-bottom">
                      <span
                        className={`orders-mobile-list-status user-orders-status-${toClassSlug(
                          record.status,
                        )}`}
                      >
                        {record.status}
                      </span>
                      <span className="orders-mobile-list-date">
                        <CalendarDays size={14} strokeWidth={2} />
                        {formatLongOrderDate(
                          record.statusDate || record.placedAt,
                        )}
                      </span>
                    </div>
                  </div>
                  <ChevronRight
                    className="orders-mobile-card-chevron"
                    size={21}
                    strokeWidth={2}
                  />
                </button>
                {renderMobileOrderActions(order, record)}
              </div>
            );
          })
        ) : (
          <div className="orders-mobile-empty">
            <PackageCheck size={31} strokeWidth={1.9} />
            <strong>No orders found</strong>
            <span>Try another search or date range.</span>
          </div>
        )}
      </div>

      {!loading && !error && totalPages > 1 && (
        <footer className="orders-mobile-pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} strokeWidth={2} />
          </button>
          <span>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            aria-label="Next page"
          >
            <ChevronRight size={18} strokeWidth={2} />
          </button>
        </footer>
      )}
    </section>
  );

  const renderActionModal = () => {
    if (!showModal) return null;

    return (
      <div
        className="orders-action-overlay"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !actionLoading) {
            setShowModal(false);
          }
        }}
      >
        <section
          className="orders-action-modal"
          role="dialog"
          aria-modal="true"
        >
          <header className="orders-action-modal-header">
            <h2>
              {actionType === "CANCEL"
                ? "Cancel Order"
                : actionType === "RETURN"
                  ? "Request Return"
                  : "Request Replacement"}
            </h2>
            <button
              type="button"
              className="orders-action-modal-close"
              onClick={() => setShowModal(false)}
              disabled={actionLoading}
              aria-label="Close"
            >
              <X size={18} strokeWidth={2} />
            </button>
          </header>

          <div className="orders-action-modal-body">
            <p>
              {actionType === "CANCEL"
                ? "Are you sure you want to cancel this order? This action cannot be undone."
                : `Please provide the reason and any additional details for your ${actionType?.toLowerCase()} request.`}
            </p>
            <select
              className="orders-action-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={actionLoading}
            >
              <option value="">Select a reason</option>
              {actionType === "CANCEL" ? (
                <>
                  <option value="Changed my mind">Changed my mind</option>
                  <option value="Found a better price">
                    Found a better price
                  </option>
                  <option value="Delivery is too slow">
                    Delivery is too slow
                  </option>
                  <option value="Ordered by mistake">
                    Ordered by mistake
                  </option>
                  <option value="Other">Other</option>
                </>
              ) : (
                <>
                  <option value="Defective item">Defective item</option>
                  <option value="Wrong item received">
                    Wrong item received
                  </option>
                  <option value="Quality not as expected">
                    Quality not as expected
                  </option>
                  <option value="Damaged during transit">
                    Damaged during transit
                  </option>
                  <option value="Other">Other</option>
                </>
              )}
            </select>

            {(actionType === "RETURN" || actionType === "REPLACE") && (
              <textarea
                className="orders-action-textarea"
                placeholder="Additional details (optional)"
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                disabled={actionLoading}
              />
            )}
          </div>

          <footer className="orders-action-modal-footer">
            <button
              type="button"
              className="orders-action-cancel"
              onClick={() => setShowModal(false)}
              disabled={actionLoading}
            >
              Close
            </button>
            <button
              type="button"
              className={`orders-action-confirm ${
                actionType === "CANCEL" ? "danger" : ""
              }`}
              onClick={handleActionSubmit}
              disabled={actionLoading || !reason}
            >
              {actionLoading
                ? "Processing..."
                : actionType === "CANCEL"
                  ? "Confirm Cancel"
                  : actionType === "RETURN"
                    ? "Submit Return"
                    : "Submit Replacement"}
            </button>
          </footer>
        </section>
      </div>
    );
  };

  return (
    <div className="user-orders-responsive-root">
      <div className="user-orders-desktop-view">{renderDesktop()}</div>
      <div className="user-orders-mobile-view">{renderMobileList()}</div>
      {renderActionModal()}
      <ChatPanel
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setSelectedOrderForChat(null);
        }}
        order={selectedOrderForChat}
      />
    </div>
  );
};

export default MyOrders;
